import React from 'react';
import { extractErrorMessage } from '../../utils/errorUtils';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  helperText?: string;
}

const Input: React.FC<InputProps> = ({
  label,
  error,
  icon,
  iconRight,
  helperText,
  className = '',
  disabled,
  required,
  ...props
}) => {
  const inputCls = [
    'input-field',
    'transition-all duration-200',
    icon      ? 'pl-10' : '',
    iconRight ? 'pr-10' : '',
    error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-400/25 bg-red-50/50'
      : '',
    disabled
      ? 'bg-slate-50 text-slate-500 cursor-not-allowed border-slate-200 hover:border-slate-200'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1 font-normal">*</span>}
        </label>
      )}

      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
            <span className="text-slate-400 flex items-center">{icon}</span>
          </div>
        )}

        <input
          className={inputCls}
          disabled={disabled}
          required={required}
          {...props}
        />

        {iconRight && (
          <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none">
            <span className="text-slate-400 flex items-center">{iconRight}</span>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1.5">
          <svg className="h-3.5 w-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          {extractErrorMessage(error)}
        </p>
      )}

      {helperText && !error && (
        <p className="mt-1.5 text-xs text-slate-500">{helperText}</p>
      )}
    </div>
  );
};

export default Input;
